import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { apiCall } from '../AuthContext';
import { Check, X, ArrowRight, Layers, Zap, Shield, BarChart3, Tag, Store } from 'lucide-react';
import { SquareLogo, ShopifyLogo, EbayLogo } from '../components/BrandLogos';

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [featRef, featInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [priceRef, priceInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [compRef, compInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    apiCall('/api/subscriptions', { method: 'GET' })
      .then(r => setPlans(r.data))
      .catch(() => {});
  }, []);

  const features = [
    { icon: Tag, title: 'Smart Label Printing', desc: 'Print barcode labels with Zebra & Rollo printers at 300 DPI. Scan, price, and print in seconds.' },
    { icon: Store, title: 'Square POS Sync', desc: 'Push products directly to Square POS with one click. Manage categories and inventory.' },
    { icon: BarChart3, title: 'Shopify Integration', desc: 'List products on Shopify with shipping profiles, images, and pricing — all automated.' },
    { icon: Zap, title: 'eBay Listings', desc: 'List products on eBay with market price research, shipping details, and item specifics built in.' },
    { icon: Shield, title: 'Barcode Lookup + Cache', desc: 'Scan any barcode to auto-fill product details. Cached locally for instant repeat lookups.' },
    { icon: Layers, title: 'Multi-Channel Dashboard', desc: 'Admin analytics, batch management, user controls, and revenue tracking — all in one place.' },
  ];

  const comparisons = [
    { feature: 'List to eBay, Shopify & Square POS', us: true, them: false },
    { feature: 'eBay Sold Price Market Research', us: true, them: false },
    { feature: 'One-Click OAuth Connect (No API Keys)', us: true, them: false },
    { feature: 'Display Retail and Discounted Prices', us: true, them: false },
    { feature: 'Same-Day Customer Support', us: true, them: false },
    { feature: 'Smart Barcode Cache (Saves API Costs)', us: true, them: false },
    { feature: 'Compatible with 300 & 200 dpi Printers', us: true, them: false },
    { feature: 'Compatible with Mac & PC', us: true, them: false },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/logo.png" alt="XLabelSync" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold text-lg tracking-tight text-[#0F172A]">XLabelSync</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors">Features</a>
            <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors">Pricing</a>
            <a href="#compare" className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors">Compare</a>
            <Link to="/contact-us" className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors">Contact</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login-btn" className="text-sm font-medium text-[#0F172A] hover:text-[#0052FF] transition-colors">Log in</Link>
            <Link to="/login" data-testid="landing-signup-btn" className="bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-5 py-2 text-sm transition-colors">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="pt-32 pb-20 sm:pt-40 sm:pb-28 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0052FF]/10 text-[#0052FF] text-xs font-semibold tracking-wide mb-6">
              <Zap className="w-3 h-3" /> SHOPIFY + SQUARE + EBAY
            </div>
            <h1 className="font-cabinet text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
              Scan, print &<br />
              <span className="text-[#0052FF]">list anywhere.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-lg">
              Scan products, print labels, and list to eBay, Shopify & Square POS — all from one platform. Built for retailers and resellers who move fast.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/login" data-testid="hero-get-started-btn" className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-6 py-3 text-sm transition-colors">
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact-us" className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-[#0F172A] border border-slate-200 font-medium rounded-lg px-6 py-3 text-sm transition-colors">
                Contact Sales
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={heroInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.7, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1770013413878-2530e2c3d82b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxwZXJzb24lMjBzY2FubmluZyUyMGJhcmNvZGUlMjBwcm9kdWN0JTIwc2hpcHBpbmclMjBlY29tbWVyY2UlMjBsYXB0b3B8ZW58MHx8fHwxNzc2NDY1NjUwfDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Product listing and shipping"
                className="rounded-2xl shadow-2xl shadow-slate-200/60 w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">Listed on eBay</p>
                    <p className="text-xs text-slate-500">Label printed + synced</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" ref={featRef} className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={featInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-3">FEATURES</p>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
              Everything you need to manage products
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 20 }} animate={featInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-lg hover:border-slate-300 transition-all duration-200"
                  data-testid={`feature-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0052FF]/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#0052FF]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" ref={priceRef} className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={priceInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-3">PRICING</p>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
              Simple, transparent pricing
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
            {plans.filter(p => p.subscription_id !== '1000').sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0)).map((plan, i) => {
              const isPopular = !!plan.is_popular;
              const isContact = !!plan.is_contact;
              const accent = plan.subscription_id === '1001' ? { dot: 'bg-green-500', text: 'text-green-600', border: 'border-green-200' }
                : plan.subscription_id === '1002' ? { dot: 'bg-[#0052FF]', text: 'text-[#0052FF]', border: 'border-[#0052FF]' }
                : plan.subscription_id === '1003' ? { dot: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-200' }
                : { dot: 'bg-red-500', text: 'text-red-600', border: 'border-red-200' };
              return (
                <motion.div key={plan.subscription_id}
                  initial={{ opacity: 0, y: 30 }} animate={priceInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`relative bg-white rounded-2xl p-7 border transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${isPopular ? `${accent.border} border-2 shadow-xl shadow-blue-100/40` : 'border-slate-200 shadow-sm'}`}
                  data-testid={`pricing-card-${plan.subscription_id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#0052FF] text-white text-[10px] font-bold tracking-wider rounded-full">
                      ⭐ MOST POPULAR
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2.5 h-2.5 rounded-full ${accent.dot}`}></span>
                    <h3 className="text-xl font-bold text-[#0F172A]">{plan.subscription_name}</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-5 min-h-[32px]">{plan.tagline || ''}</p>
                  <div className="mb-5">
                    {isContact ? (
                      <span className="text-3xl font-black text-[#0F172A]">Custom</span>
                    ) : (
                      <>
                        <span className="text-4xl font-black text-[#0F172A]">${plan.subscription_price}</span>
                        <span className="text-sm text-slate-500">/mo</span>
                      </>
                    )}
                  </div>
                  <ul className="space-y-2.5 mb-6">
                    {(plan.features || []).map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.badge && (
                    <p className={`text-xs italic mb-4 ${accent.text}`}>“{plan.badge}”</p>
                  )}
                  <Link to={isContact ? '/contact' : '/login'}
                    className={`block w-full text-center py-2.5 rounded-lg font-semibold text-sm transition-colors ${isPopular ? 'bg-[#0052FF] hover:bg-[#0040CC] text-white shadow-md shadow-blue-200' : isContact ? 'bg-slate-900 hover:bg-black text-white' : 'bg-slate-100 hover:bg-slate-900 hover:text-white text-[#0F172A]'}`}
                    data-testid={`pricing-cta-${plan.subscription_id}`}
                  >
                    {isContact ? 'Contact Us' : `Start ${plan.subscription_name} Plan`}
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" ref={compRef} className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={compInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-3">COMPARE</p>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
              Why choose XLabelSync?
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={compInView ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="comparison-table">
              <div className="grid grid-cols-3 bg-slate-50 px-6 py-4 border-b border-slate-200">
                <span className="text-sm font-semibold text-[#0F172A]">Feature</span>
                <span className="text-sm font-semibold text-[#0052FF] text-center">XLabelSync</span>
                <span className="text-sm font-semibold text-slate-400 text-center">Others</span>
              </div>
              {comparisons.map((c, i) => (
                <div key={i} className={`grid grid-cols-3 px-6 py-3.5 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} ${i < comparisons.length - 1 ? 'border-b border-slate-100' : ''}`}>
                  <span className="text-sm text-[#0F172A] font-medium">{c.feature}</span>
                  <span className="text-center">{c.us ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-red-400 mx-auto" />}</span>
                  <span className="text-center">{c.them ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-red-400 mx-auto" />}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-cabinet text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mb-4">
            Ready to streamline your business?
          </h2>
          <p className="text-lg text-slate-500 mb-8">Start your free trial today. No credit card required.</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/login" className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-8 py-3 transition-colors" data-testid="cta-get-started-btn">
              Get Started Free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact-us" className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-[#0F172A] border border-slate-200 font-medium rounded-lg px-8 py-3 transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-slate-200 py-16 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
                <img src="/logo.png" alt="XLabelSync" className="w-8 h-8 object-cover" />
              </div>
              <span className="font-bold text-lg text-white">XLabelSync</span>
            </div>
            <p className="text-sm text-slate-400">Scan, print, list anywhere. The all-in-one platform for retailers and resellers.</p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm">Pages</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">Home</Link></li>
              <li><a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a></li>
              <li><Link to="/contact-us" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm">Integrations</h4>
            <ul className="space-y-2">
              <li><Link to="/integrations/square" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-2"><SquareLogo className="w-4 h-4" /> Square POS</Link></li>
              <li><Link to="/integrations/shopify" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-2"><ShopifyLogo className="w-4 h-4" /> Shopify</Link></li>
              <li><Link to="/integrations/ebay" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-2"><EbayLogo className="w-4 h-4" /> eBay</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-white/10">
          <p className="text-sm text-slate-500">&copy; 2026 XLabelSync. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
